import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Modal,
  Tag,
  message,
  Drawer,
  Descriptions,
  Typography,
  Input,
  Select,
  Form,
  DatePicker,
  Switch,
  Popconfirm,
  Row,
  Col,
  Card,
  Alert,
  Statistic,
} from 'antd';
import {
  SearchOutlined,
  EyeOutlined,
  LockOutlined,
  UnlockOutlined,
  SafetyOutlined,
  CloseCircleOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  UserOutlined,
  ExportOutlined,
  CrownOutlined,
} from '@ant-design/icons';
import { registrarService } from '../../services/registrarService';
import userService from '../../services/userService';
import UserSelect from '../../components/UserSelect';
import DomainStatusTag from '../../../shared/components/DomainStatusTag';
import moment from 'moment';
import dayjs from 'dayjs';
import { currencySymbol, PLATFORM_CURRENCY } from '../../../shared/utils/money';

const { Title, Text } = Typography;

export default function UserDomains() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialUserId = searchParams.get('user_id') ? Number(searchParams.get('user_id')) : null;

  const [loading, setLoading] = useState(false);
  const [domains, setDomains] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [filters, setFilters] = useState(initialUserId ? { user_id: initialUserId } : {});
  const [registrars, setRegistrars] = useState([]);

  // 用户聚焦模式（admin 在审查某个具体用户）
  const [focusedUser, setFocusedUser] = useState(null);
  const [focusedUserLoading, setFocusedUserLoading] = useState(false);

  // Add/Edit Modal
  const [modalVisible, setModalVisible] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit'
  const [editingDomain, setEditingDomain] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchDomains(filters);
    fetchRegistrars();
  }, []);

  // 监听 ?user_id 变更
  useEffect(() => {
    const uid = searchParams.get('user_id');
    if (uid) {
      fetchFocusedUser(Number(uid));
    } else {
      setFocusedUser(null);
    }
  }, [searchParams]);

  const fetchFocusedUser = async (uid) => {
    setFocusedUserLoading(true);
    try {
      const resp = await userService.getUser(uid);
      if (resp.success) {
        setFocusedUser(resp.data);
      }
    } catch (e) {
      // non-fatal
    } finally {
      setFocusedUserLoading(false);
    }
  };

  const clearFocus = () => {
    const np = new URLSearchParams(searchParams);
    np.delete('user_id');
    setSearchParams(np);
    setFocusedUser(null);
    const next = { ...filters };
    delete next.user_id;
    setFilters(next);
    fetchDomains(next);
  };

  const handleImpersonate = () => {
    if (!focusedUser) return;
    // 暂未实现完整 impersonation，先用 ?as_admin=1 标记跳到 user 端
    const url = `https://console.yeap.cloud/user/domains?as_admin=1&user_id=${focusedUser.id}`;
    window.open(url, '_blank', 'noopener');
  };

  const fetchDomains = async (params = {}) => {
    setLoading(true);
    try {
      const response = await registrarService.getUserDomains(params);
      if (response.success) {
        setDomains(response.data?.list || response.data || []);
      }
    } catch (error) {
      message.error('获取域名列表失败');
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrars = async () => {
    try {
      const response = await registrarService.getRegistrars();
      if (response.success) {
        setRegistrars(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load registrars:', error);
    }
  };

  const handleViewDetails = async (domain) => {
    setSelectedDomain(null);
    setDrawerVisible(true);

    try {
      const response = await registrarService.getUserDomain(domain.id);
      if (response.success) {
        setSelectedDomain(response.data);
      }
    } catch (error) {
      message.error('获取域名详情失败');
    }
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    if (!value) {
      delete newFilters[key];
    }
    setFilters(newFilters);
    fetchDomains(newFilters);
  };

  // 防御性解析 nameservers：可能是数组、JSON 字符串，或逗号/换行分隔的纯字符串
  const parseNameservers = (ns) => {
    if (!ns) return [];
    if (Array.isArray(ns)) return ns;
    if (typeof ns === 'string') {
      const trimmed = ns.trim();
      if (!trimmed) return [];
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // 非 JSON，按逗号/换行拆分
      }
      return trimmed.split(/[\n,]+/).map((s) => s.trim()).filter(Boolean);
    }
    return [];
  };

  const getDaysUntilExpiry = (expiresAt) => {
    if (!expiresAt) return null;
    const days = moment(expiresAt).diff(moment(), 'days');
    return days;
  };

  // Extract TLD from domain
  const extractTld = (domain) => {
    if (!domain) return '';
    const parts = domain.toLowerCase().split('.');
    if (parts.length >= 2) {
      return parts.slice(1).join('.');
    }
    return '';
  };

  // Open Add Modal
  const handleAdd = () => {
    setModalMode('add');
    setEditingDomain(null);
    form.resetFields();
    form.setFieldsValue({
      status: 'active',
      auto_renew: false,
      whois_privacy: false,
      domain_lock: false,
    });
    setModalVisible(true);
  };

  // Open Edit Modal
  const handleEdit = (record) => {
    setModalMode('edit');
    setEditingDomain(record);

    const formData = {
      user_id: record.user_id,
      domain: record.domain,
      registrar_id: record.registrar_id,
      status: record.status,
      registrar_order_id: record.registrar_order_id,
      expires_at: record.expires_at ? dayjs(record.expires_at) : null,
      registered_at: record.registered_at ? dayjs(record.registered_at) : null,
      auto_renew: record.auto_renew || false,
      whois_privacy: record.whois_privacy || false,
      domain_lock: record.domain_lock || false,
      nameservers: Array.isArray(record.nameservers)
        ? record.nameservers.join(', ')
        : (typeof record.nameservers === 'string' ? record.nameservers : ''),
    };

    form.setFieldsValue(formData);
    setModalVisible(true);
  };

  // Handle Delete
  const handleDelete = async (record) => {
    try {
      const response = await registrarService.deleteUserDomain(record.id);
      if (response.success || response.code === 0) {
        message.success('域名记录已删除');
        fetchDomains(filters);
      } else {
        message.error(response.message || '删除失败');
      }
    } catch (error) {
      message.error('删除失败：' + (error.message || ''));
    }
  };

  // Handle Modal Submit
  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const values = await form.validateFields();

      // Build payload
      const payload = {
        user_id: values.user_id,
        domain: values.domain.toLowerCase().trim(),
        registrar_id: values.registrar_id,
        tld: extractTld(values.domain),
        status: values.status,
        registrar_order_id: values.registrar_order_id || null,
        auto_renew: values.auto_renew || false,
        whois_privacy: values.whois_privacy || false,
        domain_lock: values.domain_lock || false,
      };

      if (values.expires_at) {
        payload.expires_at = values.expires_at.format('YYYY-MM-DD HH:mm:ss');
      }
      if (values.registered_at) {
        payload.registered_at = values.registered_at.format('YYYY-MM-DD HH:mm:ss');
      }
      if (values.nameservers) {
        payload.nameservers = values.nameservers.split(',').map(ns => ns.trim()).filter(Boolean);
      }

      let response;
      if (modalMode === 'edit' && editingDomain) {
        payload.id = editingDomain.id;
        response = await registrarService.updateUserDomain(payload);
      } else {
        response = await registrarService.createUserDomain(payload);
      }

      if (response.success || response.code === 0) {
        message.success(modalMode === 'edit' ? '域名更新成功' : '域名创建成功');
        setModalVisible(false);
        fetchDomains(filters);
      } else {
        message.error(response.message || '操作失败');
      }
    } catch (error) {
      if (error.errorFields) {
        message.error('请完善表单信息');
      } else {
        message.error('操作失败：' + (error.message || ''));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const columns = [
    {
      title: '域名',
      dataIndex: 'domain',
      key: 'domain',
      width: 200,
      fixed: 'left',
      render: (domain, record) => (
        <Space direction="vertical" size={0}>
          <a onClick={() => handleViewDetails(record)}>{record.domain_unicode || domain}</a>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.tld_unicode || record.tld}
          </Text>
        </Space>
      ),
    },
    {
      title: '用户',
      key: 'user',
      width: 150,
      render: (_, record) => (
        <Space direction="vertical" size={0}>
          <Text>{record.user?.username || record.username}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            ID: {record.user_id}
          </Text>
        </Space>
      ),
    },
    {
      title: '注册商',
      key: 'registrar_name',
      width: 120,
      render: (_, record) => record.registrar?.name || record.registrar_name || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => <DomainStatusTag status={status} />,
    },
    {
      title: '注册日期',
      dataIndex: 'registered_at',
      key: 'registered_at',
      width: 120,
      render: (date) => (date ? moment(date).format('YYYY-MM-DD') : '-'),
    },
    {
      title: '到期日期',
      dataIndex: 'expires_at',
      key: 'expires_at',
      width: 150,
      render: (date, record) => {
        if (!date) return '-';
        const days = getDaysUntilExpiry(date);
        const dateStr = moment(date).format('YYYY-MM-DD');

        if (days === null) return dateStr;

        let color = 'default';
        if (days < 0) color = 'error';
        else if (days < 30) color = 'warning';
        else if (days < 90) color = 'processing';

        return (
          <Space direction="vertical" size={0}>
            <Text>{dateStr}</Text>
            {days >= 0 && (
              <Tag color={color} style={{ fontSize: 11 }}>
                剩余 {days} 天
              </Tag>
            )}
            {days < 0 && (
              <Tag color={color} style={{ fontSize: 11 }}>
                已过期 {Math.abs(days)} 天
              </Tag>
            )}
          </Space>
        );
      },
      sorter: (a, b) => moment(a.expires_at).diff(moment(b.expires_at)),
    },
    {
      title: '功能',
      key: 'features',
      width: 150,
      render: (_, record) => (
        <Space wrap>
          {record.auto_renew && (
            <Tag color="blue" icon={<SafetyOutlined />}>
              自动续费
            </Tag>
          )}
          {!!record.privacy_mode && (
            <Tag color="purple" icon={<SafetyOutlined />}>
              隐私模式
            </Tag>
          )}
          {record.whois_privacy && (
            <Tag color="green" icon={<SafetyOutlined />}>
              隐私保护
            </Tag>
          )}
          {record.domain_lock && (
            <Tag color="orange" icon={<LockOutlined />}>
              已锁定
            </Tag>
          )}
        </Space>
      ),
    },
    {
      title: '操作',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleViewDetails(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条域名记录吗？"
            onConfirm={() => handleDelete(record)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {focusedUser && (
        <Card
          size="small"
          style={{ marginBottom: 12 }}
          title={
            <Space>
              <UserOutlined />
              <Text strong>正在以管理员身份查看用户：{focusedUser.username || `User #${focusedUser.id}`}</Text>
            </Space>
          }
          extra={
            <Space>
              <Button icon={<ExportOutlined />} onClick={handleImpersonate} data-testid="impersonate-btn">
                以用户身份打开
              </Button>
              <Button onClick={clearFocus} data-testid="clear-focus-btn">清除聚焦</Button>
            </Space>
          }
          data-testid="focused-user-header"
        >
          <Row gutter={12}>
            <Col span={6}><Statistic title="用户ID" value={focusedUser.id} /></Col>
            <Col span={6}><Statistic title="邮箱" value={focusedUser.email || '-'} valueStyle={{ fontSize: 14 }} /></Col>
            <Col span={6}><Statistic title="余额" value={focusedUser.balance ?? '-'} precision={focusedUser.balance != null ? 2 : undefined} prefix={focusedUser.balance != null ? currencySymbol(focusedUser.balance_currency || PLATFORM_CURRENCY) : ''} /></Col>
            <Col span={6}><Statistic title="当前域名数" value={domains.length} /></Col>
          </Row>
        </Card>
      )}

      <Alert
        style={{ marginBottom: 12 }}
        type="info"
        showIcon
        icon={<CrownOutlined />}
        message={focusedUser
          ? `管理员视图 — 已聚焦到用户 ${focusedUser.username || focusedUser.id} 的域名`
          : '管理员视图 — 显示全平台所有用户的域名。手动添加 / 编辑 / 删除 仅修改本地数据库记录，不会调用注册商 API。'}
      />

      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Title level={3} style={{ margin: 0 }}>用户域名管理</Title>
        <Space wrap>
          <Input
            placeholder="搜索域名"
            prefix={<SearchOutlined />}
            style={{ width: 200 }}
            allowClear
            onChange={(e) => handleFilterChange('search', e.target.value)}
          />

          <Select
            placeholder="状态"
            style={{ width: 120 }}
            allowClear
            onChange={(value) => handleFilterChange('status', value)}
          >
            <Select.Option value="pending">待激活</Select.Option>
            <Select.Option value="active">正常</Select.Option>
            <Select.Option value="expired">已过期</Select.Option>
            <Select.Option value="suspended">已暂停</Select.Option>
            <Select.Option value="transferred">已转出</Select.Option>
            <Select.Option value="cancelled">已取消</Select.Option>
          </Select>

          <Select
            placeholder="隐私模式"
            style={{ width: 120 }}
            allowClear
            onChange={(value) => handleFilterChange('privacy_mode', value)}
          >
            <Select.Option value="1">隐私模式</Select.Option>
            <Select.Option value="0">普通</Select.Option>
          </Select>

          <Input
            placeholder="用户ID"
            style={{ width: 120 }}
            allowClear
            value={filters.user_id || ''}
            onChange={(e) => {
              const v = e.target.value;
              handleFilterChange('user_id', v);
              const np = new URLSearchParams(searchParams);
              if (v) np.set('user_id', v); else np.delete('user_id');
              setSearchParams(np);
            }}
            data-testid="user-id-input"
          />

          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd} data-testid="add-domain-btn">
            添加域名
          </Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={domains}
        rowKey="id"
        loading={loading}
        pagination={{
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条记录`,
        }}
        scroll={{ x: 1500 }}
      />

      {/* View Details Drawer */}
      <Drawer
        title="域名详情"
        placement="right"
        width={700}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        extra={
          selectedDomain && (
            <Button type="primary" icon={<EditOutlined />} onClick={() => {
              setDrawerVisible(false);
              handleEdit(selectedDomain);
            }}>
              编辑
            </Button>
          )
        }
      >
        {selectedDomain && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <Descriptions title="基本信息" column={1} bordered>
              <Descriptions.Item label="域名">
                <Text strong style={{ fontSize: 16 }}>
                  {selectedDomain.domain}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="TLD">
                <Tag color="blue">{selectedDomain.tld}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="状态">
                <DomainStatusTag status={selectedDomain.status} />
              </Descriptions.Item>
              <Descriptions.Item label="用户">
                {selectedDomain.user?.username || selectedDomain.username} (ID: {selectedDomain.user_id})
              </Descriptions.Item>
              <Descriptions.Item label="注册商">
                {selectedDomain.registrar?.name || selectedDomain.registrar_name}
              </Descriptions.Item>
              <Descriptions.Item label="注册商订单ID">
                {selectedDomain.registrar_order_id || '-'}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="日期信息" column={1} bordered>
              <Descriptions.Item label="注册日期">
                {selectedDomain.registered_at
                  ? moment(selectedDomain.registered_at).format(
                      'YYYY-MM-DD HH:mm:ss'
                    )
                  : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="到期日期">
                <Space>
                  {selectedDomain.expires_at
                    ? moment(selectedDomain.expires_at).format(
                        'YYYY-MM-DD HH:mm:ss'
                      )
                    : '-'}
                  {selectedDomain.expires_at && (
                    <Tag
                      color={
                        getDaysUntilExpiry(selectedDomain.expires_at) < 30
                          ? 'warning'
                          : 'processing'
                      }
                    >
                      剩余{' '}
                      {getDaysUntilExpiry(selectedDomain.expires_at)} 天
                    </Tag>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {moment(selectedDomain.created_at).format(
                  'YYYY-MM-DD HH:mm:ss'
                )}
              </Descriptions.Item>
              <Descriptions.Item label="更新时间">
                {moment(selectedDomain.updated_at).format(
                  'YYYY-MM-DD HH:mm:ss'
                )}
              </Descriptions.Item>
            </Descriptions>

            <Descriptions title="功能设置" column={1} bordered>
              <Descriptions.Item label="自动续费">
                {selectedDomain.auto_renew ? (
                  <Tag color="success" icon={<SafetyOutlined />}>
                    已开启
                  </Tag>
                ) : (
                  <Tag color="default" icon={<CloseCircleOutlined />}>
                    已关闭
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="WHOIS隐私保护">
                {selectedDomain.whois_privacy ? (
                  <Tag color="success" icon={<SafetyOutlined />}>
                    已启用
                  </Tag>
                ) : (
                  <Tag color="default" icon={<CloseCircleOutlined />}>
                    未启用
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="域名锁定">
                {selectedDomain.domain_lock ? (
                  <Tag color="warning" icon={<LockOutlined />}>
                    已锁定
                  </Tag>
                ) : (
                  <Tag color="default" icon={<UnlockOutlined />}>
                    未锁定
                  </Tag>
                )}
              </Descriptions.Item>
            </Descriptions>

            {parseNameservers(selectedDomain.nameservers).length > 0 && (
                <Descriptions title="DNS服务器" column={1} bordered>
                  {parseNameservers(selectedDomain.nameservers).map((ns, index) => (
                    <Descriptions.Item
                      key={index}
                      label={`DNS ${index + 1}`}
                    >
                      {ns}
                    </Descriptions.Item>
                  ))}
                </Descriptions>
              )}

            {selectedDomain.invoice_id && (
              <Descriptions title="账单信息" column={1} bordered>
                <Descriptions.Item label="发票ID">
                  {selectedDomain.invoice_id}
                </Descriptions.Item>
              </Descriptions>
            )}
          </Space>
        )}
      </Drawer>

      {/* Add/Edit Modal */}
      <Modal
        title={modalMode === 'edit' ? '编辑域名（管理员）' : '添加域名（管理员）'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleSubmit}
        confirmLoading={submitting}
        width={700}
        destroyOnClose
        okButtonProps={{ 'data-testid': 'save-domain-btn' }}
      >
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={modalMode === 'edit'
            ? '直接修改数据库记录 — 不会同步到注册商'
            : '仅插入本地记录 — 不会真实注册域名'}
          description="此面板用于人工修复 / 迁移 / 录入数据；要做真实的注册商操作，请走域名管理页的「强制续费 / 同步 / Hold」等动作。"
        />
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="域名"
                name="domain"
                rules={[
                  { required: true, message: '请输入域名' },
                  { pattern: /^[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]*[a-zA-Z0-9])?)+$/, message: '域名格式不正确' }
                ]}
              >
                <Input
                  placeholder="example.com"
                  disabled={modalMode === 'edit'}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="所属用户"
                name="user_id"
                rules={[{ required: true, message: '请选择用户' }]}
              >
                <UserSelect
                  placeholder="选择用户"
                  disabled={modalMode === 'edit'}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="注册商"
                name="registrar_id"
                rules={[{ required: true, message: '请选择注册商' }]}
              >
                <Select placeholder="选择注册商" showSearch optionFilterProp="children">
                  {registrars.map(r => (
                    <Select.Option key={r.id} value={r.id}>
                      {r.name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="状态"
                name="status"
                rules={[{ required: true, message: '请选择状态' }]}
              >
                <Select>
                  <Select.Option value="pending">待激活</Select.Option>
                  <Select.Option value="active">正常</Select.Option>
                  <Select.Option value="expired">已过期</Select.Option>
                  <Select.Option value="suspended">已暂停</Select.Option>
                  <Select.Option value="transferred">已转出</Select.Option>
                  <Select.Option value="cancelled">已取消</Select.Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="注册商订单ID"
                name="registrar_order_id"
                extra="注册商分配的域名ID或订单号"
              >
                <Input placeholder="可选" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="DNS 服务器"
                name="nameservers"
                extra="多个用逗号分隔"
              >
                <Input placeholder="ns1.example.com, ns2.example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="注册日期"
                name="registered_at"
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  placeholder="选择注册日期"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="到期日期"
                name="expires_at"
              >
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  placeholder="选择到期日期"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="自动续费"
                name="auto_renew"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="WHOIS 隐私保护（仅本地记录）"
                name="whois_privacy"
                valuePropName="checked"
                tooltip="仅修改本地数据库字段，不会调用注册商。要真正开关隐私请在域名详情页操作。"
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="域名锁定（仅本地记录）"
                name="domain_lock"
                valuePropName="checked"
                tooltip="仅修改本地数据库字段，不会调用注册商。要真正锁定/解锁转移请在域名详情页操作。"
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  );
}
